import { RadioInput } from 'components/form/Radio';
import { RadioWrapper, Label } from 'components/TimeWindow';
import { dateToFormat, serverDateToFormat } from 'helpers/formatters/date';
import {
  RadioButtons,
  RadioButtonsItem,
} from 'modules/ManageJobs/Create/CreateStyles';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Control } from 'components/form';
import { Controller } from 'react-hook-form';
import { Input } from 'components/form';
import { Select } from 'components/Select';
import { Benchmark, BenchmarkTypes } from 'types/Benchmark';
import lookup from 'api/lookup';
import { AlertStyle, BenchmarkFormStyles } from './BenchmarkFormStyles';
import { Collapsible } from 'components/form/Collapsible/Collapsible';
import Tooltip from 'components/Tooltip/Tooltip';
import { monthsOptions } from 'helpers/monthsOptions';
import admin from 'api/admin';
import { Alert } from 'react-bootstrap';
import { ExclamationCircleFill, InfoCircle } from 'react-bootstrap-icons';
import { DatePicker } from 'components/DatePicker';
import { DatePickerStyled } from 'modules/Benchmarks/Admin/AdminStyles';
import { endOfMonth } from 'date-fns';
import { capitalize } from 'lodash';
import { TextArea } from 'components/TextArea/TextArea';
import { ControlInfo } from 'components/form/Control/ControlStyles';
import { InfoOutline } from 'components/Icons';
import { IconWrapper } from 'components/IconWrapper/IconWrapper';
import { ErrorMessage } from 'components';
import { BenchmarkDetailsErrors } from 'helpers/errorMessages';
import { lookThroughDepthOptions } from '../../../../types/LookThroughDepthOptions';

const rebalanceFrequencyCustomMonthsOption = 6;

export enum CalculationFrequency {
  day = 1,
  month = 2,
  quarter = 3,
}

export enum NumberOfDaysInYear {
  days_365 = 1,
  days_252 = 2,
}

export enum RebalanceFrequency {
  never = 1,
  daily = 2,
  monthly = 3,
  quarterly = 4,
  yearly = 5,
  custom = 6,
}

type Property = {
  benchmark?: Benchmark;
  benchmarkType?: BenchmarkTypes;
  onLoadComplete?: () => void;
};

const advancedOptions: Record<string, string> = {
  pushTimeWindowsToPortfolioAnalytics: 'pushTimeWindowsToPortfolioAnalytics',
  includeBookedValues: 'includeBookedValues',
  useDailyToMonthlyLinking: 'useDailyToMonthlyLinking',
  performanceVariationCode: 'performanceVariationCode',
  portfolioAnalyticsCalendarId: 'portfolioAnalyticsCalendarId',
};

export const BenchmarkForm = (properties: Property) => {
  const { benchmark, benchmarkType, onLoadComplete } = properties;
  const {
    control,
    register,
    setValue,
    formState: { errors, dirtyFields },
    resetField,
    clearErrors,
    getValues,
  } = useFormContext();

  const calcFreqIdWatch = useWatch({ control, name: 'calculationFrequencyId' });
  const rebalanceFreqIdWatch = useWatch({
    control,
    name: 'rebalanceFrequencyId',
  });
  const [displayDailyToMonthlyLinking, setDisplayDailyToMonthlyLinking] =
    useState<boolean | null>(null);

  const [showAlert, setShowAlert] = useState(benchmark !== undefined);

  const [isSetLagDateControlVisible, setSetLagDateControlVisible] =
    useState(false);

  const [hideNumberOfDaysInYear, setHideNumberOfDaysInYear] = useState(false);

  const [hideCalendarType, setHideCalendarType] = useState(false);

  const [hideCustomMonths, setHideCustomMonths] = useState(
    benchmark === undefined
  );

  const [disableCustomLagDateSelector, setDisableCustomLagDateSelector] =
    useState(true);

  const [expandAdvancedOptions, setExpandAdvancedOptions] = useState(false);

  const [showLookThroughDepth, setShowLookThroughDepth] = useState(false);

  const { data: baseCurrencyOptions, isLoading: CurrencyOptionsLoading } =
    lookup.GetCurrencies();

  const { isLoading: numberOfDaysInYearLoading } =
    lookup.GetNumberOfDaysInYear();

  const { data: calculationFrequencies, isLoading: CalcFrequenciesLoading } =
    lookup.GetCalculationFrequencies();

  const { data: calendarOptions, isLoading: calendarOptionsLoading } =
    lookup.GetCalendars();

  const {
    data: rebalanceFrequencyOptions,
    isLoading: rebalanceFrequencyLoading,
  } = lookup.GetRebalanceFrequencies();

  const { data: weightTypeOptions, isLoading: weightTypeOptionsLoading } =
    lookup.GetWeightingMethodologies();

  const {
    data: performanceVariationOptions,
    isLoading: performanceVariationOptionsLoading,
  } = lookup.GetPerformanceVariations();

  const { data: adminSettings, isLoading: adminSettingsLoading } =
    admin.GetAdminSettings();

  useEffect(() => {
    setValue('name', benchmark?.name ?? '');

    setValue('id', benchmark?.id);

    setValue('typeId', benchmark?.type?.id);

    if (benchmark?.baseCurrencyCode) {
      setValue('baseCurrencyCode', benchmark?.baseCurrencyCode);
    }

    setValue(
      'calculationFrequencyId',
      benchmark?.calculationFrequency?.id ?? null
    );

    setHideCalendarType(
      benchmark?.calculationFrequency?.id !== CalculationFrequency.day ?? false
    );

    setValue(
      'includeBookedValues',
      benchmark?.includeBookedValues ? 'true' : 'false' ?? 'false'
    );

    resetField('isLagged', {
      defaultValue: benchmark?.isLagged ? 'true' : 'false' ?? 'false',
    });

    setSetLagDateControlVisible(benchmark?.isLagged ?? false);

    setValue('description', benchmark?.description ?? '');

    setValue(
      'portfolioAnalyticsCalendarId',
      benchmark?.portfolioAnalyticsCalendarId ?? undefined
    );

    setValue('numberOfDaysInYearId', { default: undefined });

    if (benchmark) {
      if ('weightingMethodology' in benchmark) {
        setValue('weightingMethodologyId', benchmark.weightingMethodology?.id);
      }

      if ('performanceVariationCode' in benchmark) {
        setValue(
          'performanceVariationCode',
          benchmark.performanceVariationCode
        );
      }

      if (
        'numberOfDaysInYearId' in benchmark &&
        benchmark.type?.id === BenchmarkTypes.AbsoluteReturn
      ) {
        setValue('numberOfDaysInYearId', `${benchmark.numberOfDaysInYearId}`);
      }

      if ('useDailyToMonthlyLinking' in benchmark) {
        setValue(
          'useDailyToMonthlyLinking',
          benchmark.useDailyToMonthlyLinking ? 'true' : 'false'
        );
      } else {
        resetField('useDailyToMonthlyLinking', { defaultValue: undefined });
      }

      if ('rebalanceFrequency' in benchmark) {
        setValue('rebalanceFrequencyId', benchmark.rebalanceFrequency?.typeId);

        setHideCustomMonths(
          benchmark.rebalanceFrequency?.typeId !==
          rebalanceFrequencyCustomMonthsOption
        );

        if (benchmark.rebalanceFrequency?.customMonths) {
          const results = [
            ...Object.keys(benchmark.rebalanceFrequency?.customMonths),
          ]
            .map((x) => ({
              label: capitalize(x),
              value: benchmark.rebalanceFrequency?.customMonths[x] ? x : null,
            }))
            .filter((x) => x.value !== null);

          setValue('rebalanceFrequencyCustomMonths', results);
        }
      }

      if ('pushTimeWindowsToPortfolioAnalytics' in benchmark) {
        setValue(
          'pushTimeWindowsToPortfolioAnalytics',
          benchmark?.pushTimeWindowsToPortfolioAnalytics
            ? 'true'
            : 'false' ?? 'false'
        );

        setShowLookThroughDepth(
          benchmark?.pushTimeWindowsToPortfolioAnalytics ?? false
        );
      }

      if ('lookThroughDepth' in benchmark) {
        setValue('lookThroughDepth', benchmark?.lookThroughDepth ?? 1);
      }

      if ('useLocalCurrencyReturns' in benchmark) {
        setValue('useLocalCurrencyReturns', 
          benchmark?.useLocalCurrencyReturns ? 'true' : 'false' ?? 'false',
        );
      }
    } else {
      setValue('calculationFrequencyId', null);
      setValue('rebalanceFrequencyId', null);
      resetField('useDailyToMonthlyLinking', { defaultValue: 'false' });
      resetField('includeBookedValues', { defaultValue: 'false' });
      resetField('useLocalCurrencyReturns', {defaultValue: 'true'})
    }
  }, [benchmark, resetField, setValue]);

  useEffect(() => {
    if (benchmark?.isLagged) {
      if (benchmark?.customLagDate) {
        resetField('setCustomLagDate', { defaultValue: 'false' });
        resetField('customLagDate', { defaultValue: benchmark.customLagDate });
        setDisableCustomLagDateSelector(false);
      } else {
        resetField('setCustomLagDate', { defaultValue: 'true' });
        resetField('customLagDate', { defaultValue: undefined });
        setDisableCustomLagDateSelector(true);
      }
    }
  }, [benchmark?.isLagged, benchmark?.customLagDate, resetField]);

  const isAbsoluteReturn: boolean =
    benchmarkType === BenchmarkTypes.AbsoluteReturn ||
    benchmark?.type?.id === BenchmarkTypes.AbsoluteReturn;

  const isCompositeWeighted: boolean =
    benchmarkType === BenchmarkTypes.CompositeWeighted ||
    benchmark?.type?.id === BenchmarkTypes.CompositeWeighted;

  const isCompositeReturn: boolean =
    benchmarkType === BenchmarkTypes.CompositeReturn ||
    benchmark?.type?.id === BenchmarkTypes.CompositeReturn;

  const isAssetWeighted: boolean =
    benchmarkType === BenchmarkTypes.AssetWeighted ||
    benchmark?.type?.id === BenchmarkTypes.AssetWeighted;

  const isStaticWeighted: boolean =
    benchmarkType === BenchmarkTypes.StaticWeighted ||
    benchmark?.type?.id === BenchmarkTypes.StaticWeighted;

  useEffect(() => {
    if (isStaticWeighted || isAbsoluteReturn || isCompositeWeighted) {
      if (calcFreqIdWatch !== undefined && calcFreqIdWatch !== undefined) {
        const updatedDisplayDailyToMonthlyLinking =
          (isAbsoluteReturn || isCompositeWeighted || (isStaticWeighted &&
            [RebalanceFrequency.daily, RebalanceFrequency.monthly].includes(
              rebalanceFreqIdWatch
            ))) && calcFreqIdWatch === CalculationFrequency.day;
        setDisplayDailyToMonthlyLinking(updatedDisplayDailyToMonthlyLinking);

        if (!updatedDisplayDailyToMonthlyLinking) {
          setValue('useDailyToMonthlyLinking', 'false');
        }
      }
    }
  }, [isStaticWeighted, isAbsoluteReturn, isCompositeReturn, calcFreqIdWatch, rebalanceFreqIdWatch]);

  useEffect(() => {
    if (
      CurrencyOptionsLoading &&
      CalcFrequenciesLoading &&
      calendarOptionsLoading &&
      rebalanceFrequencyLoading &&
      weightTypeOptionsLoading &&
      performanceVariationOptionsLoading &&
      adminSettingsLoading &&
      numberOfDaysInYearLoading
    ) {
      onLoadComplete?.();
    }
  }, [
    CurrencyOptionsLoading,
    CalcFrequenciesLoading,
    calendarOptionsLoading,
    rebalanceFrequencyLoading,
    weightTypeOptionsLoading,
    performanceVariationOptionsLoading,
    adminSettingsLoading,
    numberOfDaysInYearLoading,
    onLoadComplete,
  ]);

  useEffect(() => {
    for (let error in errors) {
      if (advancedOptions[error]) {
        setExpandAdvancedOptions(true);
        break;
      }
    }
  }, [errors]);

  return (
    <>
      {showAlert ? (
        <AlertStyle>
          <Alert
            variant='warning'
            onClose={() => setShowAlert(false)}
            dismissible={true}
            data-testid='warning'
          >
            <Alert.Heading>
              <ExclamationCircleFill />
              Warning
            </Alert.Heading>
            <p>
              If the lag date is updated, the benchmark will need to be run to
              display the correct data. After saving, you will be taken to
              Manage Jobs to run the benchmark.
            </p>
          </Alert>
        </AlertStyle>
      ) : null}
      <BenchmarkFormStyles>
        <div className='d-flex flex-wrap row-gap'>
          <div className='col-auto'>
            <Control label='Name'>
              <Input
                {...register('name')}
                placeholder='Enter Name'
                error={errors.name}
                errorMessage={errors.name && errors.name.message}
                style={{ width: 250, marginTop: 5 }}
                data-testid='benchmark_name'
                onChange={(e: any) =>
                  setValue('name', e.target.value, { shouldDirty: true })
                }
                required={true}
              />
            </Control>
          </div>
          {benchmark?.id ? (
            <div className='col-auto col-2'>
              <Control label='ID'>
                <label htmlFor='id' data-testid='id' style={{ marginTop: 5 }}>
                  {benchmark?.id}
                </label>
              </Control>
            </div>
          ) : null}
          {benchmark?.type ? (
            <div className='col-auto'>
              <Control label='Benchmark Type'>
                <label
                  htmlFor='type'
                  data-testid='type'
                  style={{ marginTop: 0 }}
                >
                  {benchmark?.type?.name}
                </label>
              </Control>
            </div>
          ) : null}
          <div className='col-auto'>
            <Control label='Base Currency'>
              <Controller
                control={control}
                name='baseCurrencyCode'
                defaultValue={benchmark?.baseCurrencyCode}
                render={({ field: { onChange, value } }) => (
                  <Select
                    testid='baseCurrencyCode'
                    placeholder='Select'
                    style={{ marginTop: 5 }}
                    options={baseCurrencyOptions || []}
                    value={[...(baseCurrencyOptions || [])].filter(
                      (option: any) => option.code === value
                    )}
                    onChange={(val) => onChange(val.code)}
                    errorMessage={
                      errors.baseCurrencyCode && errors.baseCurrencyCode.message
                    }
                  />
                )}
              />
            </Control>
          </div>
          {isCompositeWeighted || isAssetWeighted ? (
            <div className='col-auto'>
              <Control label='Weight Type'>
                <Controller
                  control={control}
                  name='weightingMethodologyId'
                  render={({ field: { onChange, value } }) => (
                    <Select
                      testid='weightingMethodologyId'
                      placeholder='Select'
                      style={{ marginTop: 5 }}
                      options={weightTypeOptions || []}
                      value={[...(weightTypeOptions || [])].filter(
                        (option: any) => option.value === value
                      )}
                      onChange={(val) => onChange(val.value)}
                      errorMessage={
                        errors.weightingMethodologyId &&
                        errors.weightingMethodologyId.message
                      }
                    />
                  )}
                />
              </Control>
            </div>
          ) : null}
          <div className='col-auto'>
            <Control label='Calculation Frequency'>
              <Controller
                control={control}
                name='calculationFrequencyId'
                render={({ field: { onChange, value } }) => {
                  return (
                    <Select
                      testid='calculationFrequencyId'
                      placeholder='Select'
                      options={calculationFrequencies || []}
                      value={[...(calculationFrequencies || [])].filter(
                        (option: any) => option.id === value
                      )}
                      onChange={(val) => {
                        onChange(val.id);

                        if (val.id !== CalculationFrequency.day) {
                          setHideNumberOfDaysInYear(true);
                          setHideCalendarType(true);
                          setValue('numberOfDaysInYearId', undefined);
                          if (
                            errors.pushTimeWindowsToPortfolioAnalytics &&
                            errors.pushTimeWindowsToPortfolioAnalytics
                              .message ===
                            BenchmarkDetailsErrors.cantPushTimeWindowsToPA
                          ) {
                            clearErrors('pushTimeWindowsToPortfolioAnalytics');
                          }
                        } else {
                          setHideNumberOfDaysInYear(false);
                          setHideCalendarType(false);
                          setValue('portfolioAnalyticsCalendarId', null);
                          if (isAbsoluteReturn) {
                            setValue(
                              'numberOfDaysInYearId',
                              `${NumberOfDaysInYear.days_365}`
                            );
                          }
                        }

                        if (!isAbsoluteReturn) {
                          setValue('numberOfDaysInYearId', undefined);
                        }
                      }}
                      errorMessage={
                        errors.calculationFrequencyId &&
                        errors.calculationFrequencyId.message
                      }
                      style={{
                        marginTop: 5,
                        fontWieght: 'normal !important',
                      }}
                    />
                  );
                }}
              />
            </Control>
          </div>
          {isAbsoluteReturn &&
            !hideNumberOfDaysInYear &&
            Number(getValues('calculationFrequencyId')) ===
            CalculationFrequency.day ? (
            <div className='col-auto'>
              <Control label='No. of Days in Year'>
                <RadioButtons style={{ display: 'block', marginTop: 0 }}>
                  <RadioButtonsItem style={{ paddingBottom: 5, paddingTop: 0 }}>
                    <RadioWrapper>
                      <RadioInput
                        register={register('numberOfDaysInYearId', {
                          deps: ['calculationFrequencyId'],
                          required:
                            Number(getValues('calculationFrequencyId')) ===
                            CalculationFrequency.day,
                        })}
                        value={NumberOfDaysInYear.days_252}
                      />
                      <Label>252</Label>
                    </RadioWrapper>
                    <RadioWrapper style={{ marginTop: 5 }}>
                      <RadioInput
                        register={register('numberOfDaysInYearId', {
                          deps: ['calculationFrequencyId'],
                          required:
                            Number(getValues('calculationFrequencyId')) ===
                            CalculationFrequency.day,
                        })}
                        value={NumberOfDaysInYear.days_365}
                      />
                      <Label>365</Label>
                    </RadioWrapper>
                  </RadioButtonsItem>
                </RadioButtons>
              </Control>
            </div>
          ) : null}
          {isStaticWeighted ? (
            <div className='col-auto'>
              <Control label='Rebalance Frequency'>
                <Controller
                  control={control}
                  name='rebalanceFrequencyId'
                  render={({ field: { onChange, value } }) => (
                    <Select
                      testid='rebalanceFrequencyId'
                      placeholder='Select'
                      options={rebalanceFrequencyOptions || []}
                      value={[...(rebalanceFrequencyOptions || [])].filter(
                        (option: any) => option.value === value
                      )}
                      onChange={(val) => {
                        onChange(val.value);
                        if (
                          val.value !== rebalanceFrequencyCustomMonthsOption
                        ) {
                          setValue('rebalanceFrequencyCustomMonths', null);
                        }
                        setHideCustomMonths(
                          val.value !== rebalanceFrequencyCustomMonthsOption
                        );
                      }}
                      errorMessage={
                        errors.rebalanceFrequencyId &&
                        errors.rebalanceFrequencyId.message
                      }
                      style={{
                        marginTop: 5,
                        fontWieght: 'normal !important',
                      }}
                    />
                  )}
                />
              </Control>
            </div>
          ) : null}
        </div>
        <div className='d-flex flex-wrap row-gap' style={{ marginTop: 20 }}>
          {isStaticWeighted && !hideCustomMonths ? (
            <div className='col-auto'>
              <Control label='Months'>
                <Controller
                  control={control}
                  name='rebalanceFrequencyCustomMonths'
                  render={({ field: { onChange, value } }) => (
                    <Select
                      testid='rebalanceFrequencyCustomMonths'
                      withCheckboxes={true}
                      placeholder='Select'
                      options={monthsOptions || []}
                      value={value}
                      onChange={(val: any) => {
                        onChange(val);
                      }}
                      errorMessage={
                        errors.rebalanceFrequencyCustomMonths &&
                        errors.rebalanceFrequencyCustomMonths.message
                      }
                      isMulti={true}
                      style={{
                        marginTop: 5,
                        fontWieght: 'normal !important',
                      }}
                    />
                  )}
                />
              </Control>
            </div>
          ) : null}
          {benchmark?.inceptionDate ? (
            <div className='col-auto'>
              <Control label='Inception' style={{ marginTop: 5 }}>
                <label htmlFor='inceptionDate' data-testid='inceptionDate'>
                  {benchmark?.inceptionDate
                    ? serverDateToFormat(benchmark.inceptionDate)
                    : null}
                </label>
              </Control>
            </div>
          ) : null}
          {benchmark?.createdDate ? (
            <div className='col-auto'>
              <Control label='Created' style={{ marginTop: 5 }}>
                <label htmlFor='createdDate' data-testid='createdDate'>
                  {benchmark?.createdDate
                    ? serverDateToFormat(benchmark.createdDate)
                    : null}
                </label>
              </Control>
            </div>
          ) : null}
          <div className='col-auto'>
            <Control label='Lag Date' style={{ marginTop: 5 }}>
              <RadioButtons style={{ display: 'block', marginTop: 0 }}>
                <RadioButtonsItem style={{ paddingBottom: 5, paddingTop: 0 }}>
                  <RadioWrapper>
                    <RadioInput
                      register={register('isLagged', {
                        required: true,
                      })}
                      value='true'
                      onClick={() => {
                        setValue('isLagged', 'true');
                        setValue('setCustomLagDate', 'true', {
                          shouldDirty: true,
                        });
                        setValue('customLagDate', undefined, {
                          shouldDirty: true,
                        });
                        setSetLagDateControlVisible(true);
                        setDisableCustomLagDateSelector(true);
                        clearErrors('customLagDate');
                      }}
                    />
                    <Label>Yes</Label>
                  </RadioWrapper>
                </RadioButtonsItem>
                <RadioButtonsItem>
                  <RadioWrapper>
                    <RadioInput
                      register={register('isLagged', {
                        required: true,
                      })}
                      value='false'
                      onClick={() => {
                        setValue('isLagged', 'false');
                        if (
                          (!benchmark?.isLagged &&
                            dirtyFields.setCustomLagDate) ||
                          dirtyFields.customLagDate
                        ) {
                          resetField('setCustomLagDate');
                        }
                        setValue('customLagDate', undefined);
                        setSetLagDateControlVisible(false);
                      }}
                    />
                    <Label>No</Label>
                  </RadioWrapper>
                </RadioButtonsItem>
              </RadioButtons>
            </Control>
          </div>
          {isSetLagDateControlVisible ? (
            <div className='col-auto' style={{ width: 260 }}>
              <Control label='Set Lag Date' style={{ marginTop: 5 }}>
                <RadioButtons style={{ display: 'block', marginTop: 0 }}>
                  <RadioButtonsItem
                    style={{
                      paddingBottom: 5,
                      paddingTop: 0,
                      width: 'max-content',
                    }}
                  >
                    <RadioWrapper>
                      <RadioInput
                        register={register('setCustomLagDate', {
                          required: true,
                        })}
                        // TODO: not obvious - for 'setCustomLagDate' default state means no custom date (but value is true). also lagDate handlers should be simplified
                        value='true'
                        onClick={() => {
                          setValue('setCustomLagDate', 'true');
                          setValue('customLagDate', undefined);
                          setDisableCustomLagDateSelector(true);
                          clearErrors('customLagDate');
                        }}
                      />
                      <Label>
                        Default&nbsp;
                        <Tooltip
                          message='To set the Default Lag Date, navigate to the homepage and click on the "Admin" tab.'
                          placement='bottom'
                          followCursor={false}
                        >
                          <InfoCircle
                            size={12}
                            style={{ marginLeft: 3, marginRight: 0 }}
                          />
                        </Tooltip>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{' '}
                        <span
                          style={{ color: 'gray' }}
                          data-testid='setCustomLagDate-defaultLagDate'
                        >
                          {adminSettings?.defaultLagDate !== undefined
                            ? serverDateToFormat(adminSettings?.defaultLagDate)
                            : 'Not Set'}
                        </span>
                      </Label>
                    </RadioWrapper>
                  </RadioButtonsItem>
                  <RadioButtonsItem>
                    <RadioWrapper>
                      <RadioInput
                        register={register('setCustomLagDate')}
                        value='false'
                        onClick={() => {
                          if (
                            getValues('customLagDate') ===
                            benchmark?.customLagDate
                          ) {
                            resetField('setCustomLagDate');
                            resetField('customLagDate');
                          }
                          clearErrors('customLagDate');
                          setDisableCustomLagDateSelector(false);
                          setValue('setCustomLagDate', 'false');
                          setValue(
                            'customLagDate',
                            benchmark?.customLagDate !== undefined
                              ? serverDateToFormat(
                                benchmark.customLagDate,
                                'yyyy-MM-dd'
                              )
                              : undefined
                          );
                        }}
                      />
                      <Label style={{ marginRight: 20 }}>Custom</Label>
                      <Controller
                        control={control}
                        name='customLagDate'
                        render={({ field: { onChange, value } }) => {
                          return (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'baseline',
                              }}
                            >
                              <DatePickerStyled>
                                <DatePicker
                                  style={{
                                    width: 110,
                                  }}
                                  testid='customLagDate-customDate'
                                  onChange={(date) => {
                                    if (date !== null) {
                                      onChange(date);
                                      setValue(
                                        'customLagDate',
                                        dateToFormat(
                                          endOfMonth(date as Date),
                                          'yyyy-MM-dd'
                                        )
                                      );
                                    } else {
                                      return null;
                                    }
                                  }}
                                  selected={
                                    value !== undefined
                                      ? endOfMonth(new Date(value))
                                      : undefined
                                  }
                                  dateFormat='MMM dd yyyy'
                                  showMonthYearPicker={true}
                                  isClearable={true}
                                  placeholderText='Not Set'
                                  disabled={disableCustomLagDateSelector}
                                  hasError={
                                    !disableCustomLagDateSelector &&
                                    errors.customLagDate
                                  }
                                />
                              </DatePickerStyled>
                              {!disableCustomLagDateSelector ? (
                                <p
                                  data-testid='customLagDate-reset'
                                  style={{
                                    paddingLeft: 5,
                                    cursor: 'pointer',
                                    color: '#00aee0',
                                    fontSize: 12,
                                  }}
                                  onClick={() => {
                                    onChange(undefined);
                                  }}
                                >
                                  Reset
                                </p>
                              ) : (
                                <p></p>
                              )}
                            </div>
                          );
                        }}
                      />
                    </RadioWrapper>
                  </RadioButtonsItem>
                </RadioButtons>
              </Control>
              <ControlInfo
                className={errors.customLagDate ? 'has-error' : ''}
                hidden={!errors.customLagDate}
                style={{
                  marginTop: -20,
                  marginLeft: 90,
                }}
              >
                {errors.customLagDate ? errors.customLagDate.message : ''}
              </ControlInfo>
            </div>
          ) : null}
          {isAssetWeighted ? (
            <div className='col-auto'>
              <Control label='Use Local Currency Returns' 
              style={{ marginTop: 5 }}
              labelIcon={
                <IconWrapper>
                  <Tooltip
                    message={"If \"No\", each component benchmark's return will be converted to the currency of the blended benchmark if different. If \"Yes\", each component benchmarks's return will remain in its local currency. This is useful in cases where a portfolio is fully hedged for FX exposure."}
                    delay={300}
                  >
                    <InfoOutline fill='#8795A1' className='mx-1' />
                  </Tooltip>
                </IconWrapper>
              }>
                <RadioButtons style={{ display: 'block', marginTop: 0 }}>
                  <RadioButtonsItem style={{ paddingBottom: 5, paddingTop: 0 }}>
                    <RadioWrapper>
                      <RadioInput
                        register={register('useLocalCurrencyReturns', {
                          required: true,
                        })}
                        value='true'
                        onClick={() => {
                          setValue('useLocalCurrencyReturns', 'true');
                        }}
                      />
                      <Label>Yes</Label>
                    </RadioWrapper>
                  </RadioButtonsItem>
                  <RadioButtonsItem>
                    <RadioWrapper>
                      <RadioInput
                        register={register('useLocalCurrencyReturns', {
                          required: true,
                        })}
                        value='false'
                        onClick={() => {
                          setValue('useLocalCurrencyReturns', 'false');
                        }}
                      />
                      <Label>No</Label>
                    </RadioWrapper>
                  </RadioButtonsItem>
                </RadioButtons>
              </Control>
            </div>
          ) : null}
        </div>
        <div
          className='d-flex flex-wrap row-gap'
          style={{ marginTop: 20, width: '100%' }}
        >
          <div className='col'>
            <Control label='Description' style={{ width: '100%' }}>
              <TextArea
                {...register('description')}
                error={errors.description && errors.description.message}
                defaultValue={benchmark?.description}
                data-testid='description'
                maxLength={350}
                draggable={false}
              />
            </Control>
          </div>
        </div>
        <div className='row col-12' style={{ marginTop: 20 }}>
          <div className='col'>
            <Collapsible
              label='Advanced Options'
              expand={expandAdvancedOptions}
              setExpand={setExpandAdvancedOptions}
            >
              <div className='d-flex flex-wrap row-gap'>
                {!hideCalendarType ? (
                  <div className='col-auto'>
                    <Control label='Calendar Type'>
                      <Controller
                        control={control}
                        name='portfolioAnalyticsCalendarId'
                        render={({ field: { onChange, value } }) => (
                          <Select
                            testid='PortfolioAnalyticsCalendarId'
                            placeholder='Select'
                            options={calendarOptions || []}
                            value={[...(calendarOptions || [])].filter(
                              (option: any) => option.id === value
                            )}
                            onChange={(val) => onChange(val.id)}
                            hasError={errors.portfolioAnalyticsCalendarId}
                            errorMessage={
                              errors.portfolioAnalyticsCalendarId &&
                              errors.portfolioAnalyticsCalendarId.message
                            }
                            style={{
                              marginTop: 5,
                              minHeight: 40,
                              fontWieght: 'normal !important',
                            }}
                          />
                        )}
                      />
                    </Control>
                  </div>
                ) : null}
                {isCompositeWeighted || isAssetWeighted || isCompositeReturn ? (
                  <div className='col-auto'>
                    <Control label='Performance Variations'>
                      <Controller
                        control={control}
                        name='performanceVariationCode'
                        render={({ field: { onChange, value } }) => (
                          <Select
                            testid='performanceVariationCode'
                            placeholder='Select'
                            options={performanceVariationOptions || []}
                            value={[
                              ...(performanceVariationOptions || []),
                            ].filter((option: any) => option.code === value)}
                            onChange={(val) => onChange(val.code)}
                            errorMessage={
                              errors.performanceVariationCode &&
                              errors.performanceVariationCode.message
                            }
                            style={{
                              marginTop: 5,
                              fontWieght: 'normal !important',
                            }}
                          />
                        )}
                      />
                    </Control>
                  </div>
                ) : null}
                {displayDailyToMonthlyLinking ? (
                  <div className='col-auto'>
                    <Control label='Daily to Monthly Linking'>
                      <RadioButtons style={{ display: 'block', marginTop: 0 }}>
                        <RadioButtonsItem className='pb-1 pt-0'>
                          <RadioWrapper>
                            <RadioInput
                              register={register('useDailyToMonthlyLinking', {
                                required: true,
                              })}
                              value='true'
                              onClick={() => {
                                setValue('useDailyToMonthlyLinking', 'true');
                              }}
                            />
                            <Label>Yes</Label>
                          </RadioWrapper>
                        </RadioButtonsItem>
                        <RadioButtonsItem>
                          <RadioWrapper>
                            <RadioInput
                              register={register('useDailyToMonthlyLinking', {
                                required: true,
                              })}
                              value='false'
                              onClick={() => {
                                setValue('useDailyToMonthlyLinking', 'false');
                              }}
                            />
                            <Label>No</Label>
                          </RadioWrapper>
                        </RadioButtonsItem>
                      </RadioButtons>
                    </Control>
                  </div>
                ) : null}
                <div className='col-auto'>
                  <Control label='Include Booked Values'>
                    <RadioButtons style={{ display: 'block', marginTop: 0 }}>
                      <RadioButtonsItem className='pb-1 pt-0'>
                        <RadioWrapper>
                          <RadioInput
                            register={register('includeBookedValues', {
                              required: true,
                            })}
                            value='true'
                            onClick={() => {
                              setValue('includeBookedValues', 'true');
                            }}
                          />
                          <Label>Yes</Label>
                        </RadioWrapper>
                      </RadioButtonsItem>
                      <RadioButtonsItem>
                        <RadioWrapper>
                          <RadioInput
                            register={register('includeBookedValues', {
                              required: true,
                            })}
                            value='false'
                            onClick={() => {
                              setValue('includeBookedValues', 'false');
                            }}
                          />
                          <Label>No</Label>
                        </RadioWrapper>
                      </RadioButtonsItem>
                    </RadioButtons>
                  </Control>
                </div>
                {(isCompositeWeighted || isStaticWeighted) && (
                  <div className='col-auto'>
                    <Control
                      direction='column'
                      label='Push Time Windows'
                      style={{ marginRight: 25 }}
                      labelIcon={
                        <IconWrapper>
                          <Tooltip
                            message='If "Yes" is selected, the time windows will be pushed to Portfolio Analytics.'
                            delay={300}
                          >
                            <InfoOutline fill='#8795A1' className='mx-1' />
                          </Tooltip>
                        </IconWrapper>
                      }
                    >
                      <RadioButtons style={{ display: 'block', marginTop: 0 }}>
                        <RadioButtonsItem
                          style={{ paddingBottom: 5, paddingTop: 0 }}
                        >
                          <RadioWrapper>
                            <RadioInput
                              register={register(
                                'pushTimeWindowsToPortfolioAnalytics',
                                {
                                  required: true,
                                }
                              )}
                              value='true'
                              onClick={() => {
                                setValue(
                                  'pushTimeWindowsToPortfolioAnalytics',
                                  'true'
                                );
                                setShowLookThroughDepth(true);

                                // Set the default value of the look through depth
                                setValue('lookThroughDepth', 1);
                              }}
                            />
                            <Label>Yes</Label>
                          </RadioWrapper>
                        </RadioButtonsItem>
                        <RadioButtonsItem>
                          <RadioWrapper>
                            <RadioInput
                              register={register(
                                'pushTimeWindowsToPortfolioAnalytics',
                                {
                                  required: true,
                                }
                              )}
                              value='false'
                              onClick={() => {
                                setValue(
                                  'pushTimeWindowsToPortfolioAnalytics',
                                  'false'
                                );
                                setShowLookThroughDepth(false);
                              }}
                            />
                            <Label>No</Label>
                          </RadioWrapper>
                        </RadioButtonsItem>
                        <ErrorMessage
                          errors={errors}
                          name='pushTimeWindowsToPortfolioAnalytics'
                          styles={{ bottom: '-10px', position: 'static' }}
                        />
                      </RadioButtons>
                    </Control>
                  </div>
                )}
                {showLookThroughDepth && (
                  <div className='auto-col'>
                    <Control label='Look Through Depth'>
                      <Controller
                        control={control}
                        name='lookThroughDepth'
                        render={({ field: { onChange, value } }) => (
                          <Select
                            testid='lookThroughDepthCode'
                            style={{ marginTop: 5 }}
                            options={lookThroughDepthOptions || []}
                            value={[...(lookThroughDepthOptions || [])].filter(
                              (option: any) => option.value === value
                            )}
                            onChange={(val) => onChange(val.value)}
                            errorMessage={
                              errors.lookThroughDepth &&
                              errors.lookThroughDepth.message
                            }
                          />
                        )}
                      />
                    </Control>
                  </div>
                )}
              </div>
            </Collapsible>
          </div>
        </div>
      </BenchmarkFormStyles>
    </>
  );
};
